import mfaInfoLogo from "@assets/images/mfa-info.svg";
import mfaLogo from "@assets/images/mfa-logo.svg";
import smLogo from "@assets/images/smo-blue.svg";
import {
  Box,
  Button,
  Card,
  CssBaseline,
  Paper,
  Typography
} from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import { mfaSetupLaterMutation } from "@saleor/auth/mutations";
import {
  MfaSetupLater,
  MfaSetupLaterVariables
} from "@saleor/auth/types/MfaSetupLater";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import React from "react";
import { useApolloClient, useMutation } from "react-apollo";
import { useLocation, useNavigate } from "react-router";

import theme from "../../theme";
import useStyles from "./styles";

const MfaGetStartedPage: React.FC = () => {
  const classes = useStyles({});
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotifier();
  const apolloClient = useApolloClient();
  const { loginByToken } = useUser();

  const mfaLocalStorage = localStorage.getItem("mfaLocalStorage");
  const { mfaRemainingGraceDays, userEmail } =
    location.state || (mfaLocalStorage ? JSON.parse(mfaLocalStorage) : {});

  const [mfaSetupLater] = useMutation<MfaSetupLater, MfaSetupLaterVariables>(
    mfaSetupLaterMutation,
    {
      client: apolloClient,
      onCompleted: ({ mfaSetupLater }) => {
        if (mfaSetupLater?.errors?.length) {
          mfaSetupLater.errors.forEach(error =>
            notify({ status: "error", text: error.message })
          );
        }
      }
    }
  );

  const handleMfaSetupLater = async (email: string) => {
    try {
      const { data } = await mfaSetupLater({ variables: { email } });
      const setupLaterData = data?.mfaSetupLater;

      if (setupLaterData?.errors?.length) {
        notify({
          status: "error",
          text: "An error occurred. Please try again."
        });
        return;
      }

      loginByToken(
        setupLaterData?.token,
        setupLaterData?.csrfToken,
        setupLaterData?.user
      );
      navigate("/");
    } catch (error) {
      notify({ status: "error", text: "Failed to process request." });
    }
  };

  const handleButtonClick = (buttonType: string) => {
    if (buttonType === "First") {
      navigate("/mfa-auth-page/", { state: { userEmail } });
    } else if (buttonType === "Second") {
      handleMfaSetupLater(userEmail);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className={classes.centerContainer}>
        <img className={classes.logo} src={smLogo} alt="SM Logo" />
        <Card className={classes.card}>
          <img className={classes.logo} src={mfaLogo} alt="MFA Logo" />
          <Typography variant="h5" className={classes.title}>
            Help Secure Your Account with Us
          </Typography>
          <Typography className={classes.subtitle}>
            Your organization requires you to set up{" "}
            <b>Two-Factor Authentication (2FA)</b> for your account to reduce
            security risks and prevent unauthorized access.
          </Typography>
          <div className={classes.buttonContainer}>
            <Button
              variant="contained"
              color="primary"
              className={classes.getStartedButton}
              onClick={() => handleButtonClick("First")}
            >
              GET STARTED
            </Button>
            {mfaRemainingGraceDays !== 0 && (
              <Button
                variant="outlined"
                color="primary"
                className={classes.setupLaterButton}
                onClick={() => handleButtonClick("Second")}
              >
                SETUP LATER
              </Button>
            )}
          </div>
          <Paper className={classes.infoCard} elevation={0}>
            <img
              className={classes.infoLogo}
              src={mfaInfoLogo}
              alt="MFA Info"
            />
            <Typography style={{ fontSize: 12 }} align="left">
              <b>Need help?</b> Reach out to{" "}
              <b>smmallsonline.acquisition@smsupermalls.com</b>
            </Typography>
          </Paper>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default MfaGetStartedPage;
